import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoicesApi, clientsApi, productsApi, einvoiceApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import ProductSearchDialog from '../components/ProductSearchDialog';
import ClientSearch from '../components/ClientSearch';
import InvoiceLinesTable, { computeInvoiceTotals } from '../components/InvoiceLinesTable';
import InvoiceSdiPanel from '../components/InvoiceSdiPanel';
import { openBlob } from '../components/InvoiceStatusBadge';
import { ArrowLeft, CheckCircle2, FileCode2, PackageSearch, Plus, Printer, Save, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const errMsg = async (e, fb) => {
  let d = e.response?.data?.detail;
  if (e.response?.data instanceof Blob) {
    try { d = JSON.parse(await e.response.data.text()).detail; } catch { d = null; }
  }
  if (d?.errors) return { msg: d.message, errors: d.errors };
  if (Array.isArray(d)) return { msg: d.map((x) => x.msg).join(', '), errors: [] };
  return { msg: typeof d === 'string' ? d : fb, errors: [] };
};

const InvoiceFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [errors, setErrors] = useState([]);
  const [form, setForm] = useState({ client_id: '', date: new Date().toISOString().slice(0, 10), lines: [], payment_method: '', payment_due: '', causale: '' });

  const loadInvoice = (inv) => {
    setInvoice(inv);
    setForm({ client_id: inv.client_id, date: inv.date, lines: inv.lines || [], payment_method: inv.payment_method || '', payment_due: inv.payment_due || '', causale: inv.causale || '' });
  };

  useEffect(() => {
    (async () => {
      try {
        const [c, p, s] = await Promise.all([clientsApi.getAll(), productsApi.getAll(), einvoiceApi.getSettings()]);
        setClients(c.data); setProducts(p.data); setSettings(s.data);
        if (isEdit) loadInvoice((await invoicesApi.getById(id)).data);
        else setForm((f) => ({ ...f, payment_method: s.data.default_payment_method }));
      } catch { toast.error('Errore nel caricamento'); navigate('/fatture'); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const readOnly = isEdit && invoice && !invoice.editable;
  const set = (k, v) => setForm({ ...form, [k]: v });

  const addProduct = (product, boxes, explicitPieces) => {
    const b = parseFloat(boxes) || 0;
    if (b <= 0) return;
    const qty = explicitPieces != null ? parseFloat(explicitPieces) || 0 : Math.round(b * (parseFloat(product.pieces_per_box) || 1) * 100) / 100;
    set('lines', [...form.lines, { product_id: product.id, product_code: product.code, description: product.description, quantity: qty,
      unit: (product.unit || 'pz') === 'kg' ? 'KG' : 'PZ', price: product.price || 0, iva: product.iva ?? 22 }]);
    toast.success(`${product.code} aggiunto`, { duration: 1200 });
  };
  const addFreeLine = () => set('lines', [...form.lines, { product_code: '', description: '', quantity: 1, unit: 'PZ', price: 0, iva: 22 }]);

  const payload = () => ({ ...form, lines: form.lines.map((l) => ({ ...l, quantity: parseFloat(l.quantity) || 0, price: parseFloat(l.price) || 0, iva: parseFloat(l.iva) || 0 })) });

  const save = async () => {
    if (!form.client_id) { toast.error('Seleziona un cliente'); return null; }
    setBusy(true);
    try {
      if (isEdit) {
        const r = await invoicesApi.update(id, payload()); loadInvoice(r.data); toast.success('Fattura salvata'); return r.data;
      }
      const r = await invoicesApi.create(payload());
      loadInvoice(r.data);
      toast.success(`Fattura N. ${r.data.number} creata`); navigate(`/fatture/${r.data.id}`, { replace: true }); return r.data;
    } catch (e) { toast.error((await errMsg(e, 'Errore nel salvataggio')).msg); return null; }
    finally { setBusy(false); }
  };

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (r?.data?.id) loadInvoice(r.data); if (okMsg) toast.success(okMsg); return r; }
    catch (e) { const m = await errMsg(e, 'Operazione non riuscita'); setErrors(m.errors); toast.error(m.msg); return null; }
    finally { setBusy(false); }
  };

  const check = async () => {
    if (!(await save())) return;
    const r = await run(() => invoicesApi.check(id));
    if (!r) return;
    setErrors(r.data.errors || []);
    if (r.data.ok) toast.success(r.data.aruba_checked ? 'Fattura valida (verificata anche da Aruba)' : 'Fattura valida (controllo locale)');
    else toast.error('Ci sono dati da correggere');
  };

  const send = async () => {
    setConfirm(null);
    if (!(await save())) return;
    setErrors([]);
    await run(() => invoicesApi.send(id), 'Fattura inviata allo SDI tramite Aruba');
  };

  const remove = async () => {
    setConfirm(null);
    const r = await run(() => invoicesApi.delete(id), 'Fattura eliminata');
    if (r) navigate('/fatture');
  };

  const downloadXml = async () => {
    const r = await run(() => invoicesApi.getXml(id));
    if (r) openBlob(r.data, 'application/xml', `Fattura_${invoice.number}-${invoice.year}.xml`);
  };
  const pdf = async () => { const r = await run(() => invoicesApi.getPdf(id)); if (r) openBlob(r.data, 'application/pdf'); };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Caricamento...</div>;
  const totals = computeInvoiceTotals(form.lines);

  return (
    <div data-testid="invoice-form-page" className="space-y-4 max-w-6xl mx-auto pb-4">
      <ProductSearchDialog open={searchOpen} onOpenChange={setSearchOpen} products={products} onInsert={addProduct} existingItems={[]} allowDuplicates />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/fatture')} data-testid="back-to-invoices-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 data-testid="invoice-title" className="text-2xl font-bold tracking-tight font-['Manrope']">
              {isEdit ? `Fattura N. ${invoice?.number}/${invoice?.year}` : `Nuova fattura (N. ${settings?.next_number})`}
            </h1>
            <p className="text-muted-foreground text-sm">
              {invoice?.ddt_refs?.length ? `Fattura differita · DDT ${invoice.ddt_refs.map((r) => r.number).join(', ')}` : 'Fattura immediata'} · prezzi IVA inclusa
            </p>
          </div>
        </div>
        {isEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="invoice-pdf-btn" variant="outline" onClick={pdf} disabled={busy}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
            <Button data-testid="invoice-xml-btn" variant="outline" onClick={downloadXml} disabled={busy}><FileCode2 className="w-4 h-4 mr-2" /> XML</Button>
            {!readOnly && <>
              <Button data-testid="invoice-check-btn" variant="outline" onClick={check} disabled={busy}><CheckCircle2 className="w-4 h-4 mr-2" /> Controlla</Button>
              <Button data-testid="invoice-send-btn" onClick={() => setConfirm('send')} disabled={busy || !settings?.configured} title={settings?.configured ? '' : 'Collega Aruba nelle Impostazioni'} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Send className="w-4 h-4 mr-2" /> Invia allo SDI
              </Button>
              <Button data-testid="invoice-delete-btn" variant="ghost" size="icon" className="text-destructive" onClick={() => setConfirm('delete')} disabled={busy}><Trash2 className="w-4 h-4" /></Button>
            </>}
          </div>
        )}
      </div>

      {isEdit && invoice && <InvoiceSdiPanel invoice={invoice} errors={errors} busy={busy} onRefresh={() => run(() => invoicesApi.refreshStatus(id), 'Stato aggiornato')} />}
      {!settings?.configured && (
        <p data-testid="aruba-not-configured-hint" className="text-xs text-amber-700">Per inviare allo SDI collega il tuo account Aruba in Impostazioni → Fatturazione elettronica. Puoi comunque creare, controllare e scaricare XML e PDF.</p>
      )}

      <Card className="card-swiss">
        <CardHeader className="py-3"><CardTitle className="text-sm font-semibold font-['Manrope']">Cliente e dati documento</CardTitle></CardHeader>
        <CardContent className="pt-0 space-y-3">
          <ClientSearch clients={clients} value={form.client_id} onChange={(v) => set('client_id', v)} disabled={readOnly || Boolean(invoice?.ddt_refs?.length)} />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Data fattura</Label>
              <Input data-testid="invoice-date-input" type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="h-8 text-xs" disabled={readOnly} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Pagamento</Label>
              <Select value={form.payment_method || 'none'} onValueChange={(v) => set('payment_method', v === 'none' ? '' : v)} disabled={readOnly}>
                <SelectTrigger data-testid="invoice-payment-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non indicato</SelectItem>
                  {Object.entries(settings?.payment_methods || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Scadenza pagamento</Label>
              <Input data-testid="invoice-due-input" type="date" value={form.payment_due} onChange={(e) => set('payment_due', e.target.value)} className="h-8 text-xs" disabled={readOnly} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Causale / note</Label>
              <Input data-testid="invoice-causale-input" value={form.causale} onChange={(e) => set('causale', e.target.value)} className="h-8 text-xs" maxLength={200} disabled={readOnly} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-swiss">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-semibold font-['Manrope']">Righe</CardTitle>
            {!readOnly && (
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={addFreeLine} data-testid="add-free-line-btn" className="h-9"><Plus className="w-4 h-4 mr-1" /> Riga libera</Button>
                <Button type="button" onClick={() => setSearchOpen(true)} data-testid="invoice-add-product-btn" className="h-9 bg-green-600 hover:bg-green-700 text-white"><PackageSearch className="w-4 h-4 mr-2" /> Aggiungi prodotti</Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0"><InvoiceLinesTable lines={form.lines} onChange={(l) => set('lines', l)} readOnly={readOnly} /></CardContent>
      </Card>

      <div className="sticky bottom-0 -mx-2 px-2 py-3 bg-background/95 backdrop-blur border-t border-border z-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm flex gap-5 flex-wrap">
            <span>Imponibile <b data-testid="invoice-imponibile" className="font-['JetBrains_Mono']">{eur(totals.imponibile)}</b></span>
            <span>IVA <b data-testid="invoice-imposta" className="font-['JetBrains_Mono']">{eur(totals.imposta)}</b></span>
            <span>Totale <b data-testid="invoice-totale" className="font-['JetBrains_Mono'] text-lg">{eur(totals.totale)}</b></span>
          </div>
          {!readOnly && (
            <Button data-testid="save-invoice-btn" onClick={save} disabled={busy} className="btn-primary"><Save className="w-4 h-4 mr-2" /> {isEdit ? 'SALVA' : 'CREA BOZZA'}</Button>
          )}
        </div>
      </div>

      <AlertDialog open={Boolean(confirm)} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === 'send' ? 'Inviare la fattura allo SDI?' : 'Eliminare la fattura?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'send'
                ? `La fattura N. ${invoice?.number} di ${eur(totals.totale)} verrà trasmessa tramite Aruba (${settings?.environment === 'production' ? 'ambiente REALE' : 'ambiente di prova'}). Dopo l'invio non sarà più modificabile.`
                : 'La bozza verrà eliminata e gli eventuali DDT collegati torneranno da fatturare.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-invoice-action-btn" onClick={confirm === 'send' ? send : remove}
              className={confirm === 'send' ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-destructive hover:bg-destructive/90'}>
              {confirm === 'send' ? 'Invia' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvoiceFormPage;
